import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  uniqueIndex,
  date,
  pgEnum,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { employees, clients, dependents } from "./business";
import { plans, clientPlanEligibility } from "./plans";

export const enrollmentStatusEnum = pgEnum("enrollment_status", ["active", "terminated", "pending", "cobra", "deleted", "waived"]);

// Enhanced Employee Enrollments with Comprehensive Coverage Details
// 
// ARCHITECTURE NOTE: One row per person covered
// - Employee enrollment: dependentId = null
// - Dependent enrollment: dependentId = <id> (references dependents.id)
// Use groupEnrollmentsByPlan() utility from @/lib/enrollmentGrouping for processing
// See docs/ENROLLMENT_ARCHITECTURE.md for detailed explanation
export const enrollments = pgTable("enrollments", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull().references(() => employees.id, { onDelete: "cascade" }),
  // CRITICAL: null = employee enrollment, number = dependent enrollment
  // Always use groupEnrollmentsByPlan() utility to process enrollments
  dependentId: integer("dependent_id").references(() => dependents.id, { onDelete: "set null" }),
  planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "restrict" }),
  clientId: integer("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  coverageTier: text("coverage_tier").notNull(), // employee_only, employee_spouse, employee_child, family
  enrollmentDate: timestamp("enrollment_date").defaultNow(),
  effectiveDate: timestamp("effective_date").notNull(),
  endDate: timestamp("end_date"),
  status: enrollmentStatusEnum("status").notNull().default("active"),
  // Premium amounts are computed dynamically; keep nullable columns for backward compatibility
  employeePremium: decimal("employee_premium", { precision: 10, scale: 2 }),
  employerPremium: decimal("employer_premium", { precision: 10, scale: 2 }),
  totalPremium: decimal("total_premium", { precision: 10, scale: 2 }),
  payrollDeduction: decimal("payroll_deduction", { precision: 10, scale: 2 }),
  beneficiaryName: text("beneficiary_name"), // for life insurance
  beneficiaryRelationship: text("beneficiary_relationship"),
  // Life/disability/supplemental coverage amounts
  coverageAmount: decimal("coverage_amount", { precision: 12, scale: 2 }), // Elected coverage amount (e.g., $100,000 life)
  coverageVolume: decimal("coverage_volume", { precision: 12, scale: 2 }), // Volume-based plans (per $1,000 unit)
  annualElection: decimal("annual_election", { precision: 10, scale: 2 }), // FSA annual election amount
  hsaEligible: boolean("hsa_eligible").default(false),
  cobraEligible: boolean("cobra_eligible").default(false),
  enrollmentSource: text("enrollment_source").default("new_hire"), // new_hire, open_enrollment, qualifying_event
  waivedCoverage: boolean("waived_coverage").default(false),
  waiverReason: text("waiver_reason"),
  dependentWaiverReasons: jsonb("dependent_waiver_reasons").$type<Record<string, string>>(), // { "dependentId": "reason", ... }
  eventType: text("event_type"), // voluntary_waiver, switched_to_spouse_plan, qualifying_life_event, cobra_conversion, other
  eventDescription: text("event_description"),
  modifiedByType: text("modified_by_type"), // employee, broker
  modifiedByUserId: integer("modified_by_user_id"), // FK to users table
  lastModified: timestamp("last_modified").defaultNow(),
  signatureId: integer("signature_id"), // references signatures.id - the signature that authorized this enrollment
  
  // Enrollment workflow progress tracking (visible in employer portal)
  // Industry-standard statuses: not_started, viewed, in_progress, submitted, under_review, confirmed, declined
  enrollmentProgress: text("enrollment_progress").default("not_started"),
  progressUpdatedAt: timestamp("progress_updated_at"),
  progressNotes: text("progress_notes"), // optional notes about progress (e.g., "pending documentation")

  // Snapshot of which eligibility group this enrollment resolved to at
  // enrollment time. Preserved across later rule changes so historical
  // contributions/ACA decisions can be reconstructed. Nullable for legacy
  // pre-multi-group rows; populated by the resolver for new enrollments.
  eligibilityGroupId: integer("eligibility_group_id").references(
    () => clientPlanEligibility.id
  ),
});

// Enrollment progress statuses (industry-standard terminology)
export const ENROLLMENT_PROGRESS_STATUSES = [
  'not_started',    // Employee hasn't begun enrollment
  'viewed',         // Employee has viewed enrollment options
  'in_progress',    // Employee is actively making selections
  'submitted',      // Employee has submitted their enrollment
  'under_review',   // HR/Broker is reviewing the submission
  'confirmed',      // Enrollment has been confirmed/approved
  'declined',       // Enrollment was declined or rejected
] as const;

export type EnrollmentProgress = typeof ENROLLMENT_PROGRESS_STATUSES[number];

// Insert schemas
export const insertEnrollmentSchema = createInsertSchema(enrollments).omit({
  id: true,
  enrollmentDate: true,
  lastModified: true,
  progressUpdatedAt: true,
  // Omit premium fields - they're computed dynamically, not provided on insert
  employeePremium: true,
  employerPremium: true,
  totalPremium: true,
  payrollDeduction: true,
});

// Types
export type Enrollment = typeof enrollments.$inferSelect;
export type InsertEnrollment = z.infer<typeof insertEnrollmentSchema>;

// ---------------------------------------------------------------------------
// Enrollment drafts (autosave / resume)
// ---------------------------------------------------------------------------
// In-progress enrollment state, persisted server-side so a member — or a
// broker impersonating them — can leave and resume the flow without redoing
// elections and signatures. Kept in its own table (one row per employee per
// enrollment period) so partial state never leaks into `enrollments`, which
// only holds submitted rows. Cleared when the enrollment is submitted.
export const enrollmentDrafts = pgTable(
  "enrollment_drafts",
  {
    id: serial("id").primaryKey(),
    employeeId: integer("employee_id")
      .notNull()
      .references(() => employees.id, { onDelete: "cascade" }),
    clientId: integer("client_id").references(() => clients.id, {
      onDelete: "cascade",
    }),
    // Identifies the enrollment window this draft belongs to (e.g. an OE id,
    // a QLE effective date, or "new_hire"). Unique per employee so resume is
    // deterministic and re-saves upsert rather than duplicate.
    periodKey: varchar("period_key", { length: 100 }).notNull(),
    enrollmentSource: text("enrollment_source"), // open_enrollment | qualifying_event | new_hire
    // Serialized wizard state: selected plans by type, waivers, reasons, step.
    wizardState: jsonb("wizard_state")
      .$type<Record<string, any>>()
      .notNull()
      .default({}),
    // Signed signature-field keys from the signing view (`${formId}:${planId}:${fieldId}`).
    signedKeys: jsonb("signed_keys").$type<string[]>().notNull().default([]),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    employeePeriodUnique: uniqueIndex("enrollment_drafts_employee_period_uniq").on(
      t.employeeId,
      t.periodKey,
    ),
    employeeIdx: index("enrollment_drafts_employee_idx").on(t.employeeId),
  }),
);

export const insertEnrollmentDraftSchema = createInsertSchema(enrollmentDrafts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type EnrollmentDraft = typeof enrollmentDrafts.$inferSelect;
export type InsertEnrollmentDraft = z.infer<typeof insertEnrollmentDraftSchema>;
